import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button from '@atlaskit/button/new';
import PageHeader from '@atlaskit/page-header';
import { Box, Stack, Text } from '@atlaskit/primitives';
import { type IReportsResponse, ReportsServices } from '@shared/api';
import { formatDateTime } from '@features/partnerManagement/components/partnerListCellHelpers';
import DataTable from '@shared/ui/DataTable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewReportDialog } from './NewReportDialog';

function Reports() {
    const navigate = useNavigate();
    const [isNewReportDialogOpen, setIsNewReportDialogOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState<IReportsResponse | null>(null);
    const queryClient = useQueryClient();
    const triggerDownload = (url: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const reportColumns = useMemo<ColumnDef<IReportsResponse, unknown>[]>(
        () => [
            {
                id: 'reportName',
                header: 'Report Name',
                accessorKey: 'reportName',
                sortingFn: 'alphanumeric'
            },
            {
                id: 'dateRange',
                header: 'Date Range',
                cell: ({ row }) => `${row.original.startDate} - ${row.original.endDate}`
            },
            //   {
            //     id: 'dimensions',
            //     header: 'Dimensions',
            //     accessorKey: 'dimensions'
            //   },
            //   {
            //     id: 'metrics',
            //     header: 'Metrics',
            //     accessorKey: 'metrics'
            //   },
            //   {
            //     id: 'orgIds',
            //     header: 'Org IDs',
            //     cell: ({ row }) => [row.original.spId, row.original.dpId].filter(Boolean).join(' | ')
            //   },
            //   {
            //     id: 'filters',
            //     header: 'Filters',
            //     cell: ({ row }) =>
            //       [row.original.appId, row.original.country, row.original.adUnitType, row.original.platform]
            //         .filter(Boolean)
            //         .join(' | ')
            //   },
            {
                id: 'generatedAt',
                header: 'Date Created',
                accessorKey: 'generatedAt',
                cell: ({ row }) => formatDateTime(row.original.generatedAt)
            },
            {
                id: 'updatedAt',
                header: 'Last Updated',
                accessorKey: 'updatedAt',
                cell: ({ row }) => formatDateTime(row.original.updatedAt)
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => (
                    <Box
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'flex-start'
                        }}
                    >
                        <Button
                            appearance="subtle"
                            onClick={() => {
                                setSelectedReport(row.original);
                                setIsNewReportDialogOpen(true);
                            }}
                        >
                            Edit
                        </Button>
                        <Button
                            appearance="subtle"
                            onClick={async () => {
                                const shouldDelete = window.confirm(
                                    `Delete report "${row.original.reportName}"?`
                                );

                                if (!shouldDelete) {
                                    return;
                                }

                                await ReportsServices.deleteReportById(row.original.reportId);
                                await queryClient.invalidateQueries({ queryKey: ['allReports'] });
                            }}
                        >
                            Delete
                        </Button>
                        <Button
                            appearance="subtle"
                            onClick={async () => {
                                const response = await ReportsServices.downloadReportById(row.original.reportId);
                                triggerDownload(response);
                            }}
                        >
                            Download
                        </Button>
                    </Box>
                )
            }
        ],
        [queryClient]
    );
    const breadcrumbs = (
        <Breadcrumbs>
            <BreadcrumbsItem text="Dashboard" key="Dashboard" onClick={() => navigate('/home')} />
            <BreadcrumbsItem text="Reports" key="Reports" />
        </Breadcrumbs>
    );
    const { data: allReportsList = [] } = useQuery<IReportsResponse[]>({
        queryKey: ['allReports'],
        queryFn: () => ReportsServices.getAllReports()
    });

    return (
        <>
            <Stack space="space.300" xcss={{ width: '100%' }}>
                <PageHeader
                    breadcrumbs={breadcrumbs}
                    actions={
                        <Button appearance="primary" isDisabled={false} onClick={() => setIsNewReportDialogOpen(true)}>
                            New Report
                        </Button>
                    }
                >
                    Reports
                </PageHeader>

                <Stack space="space.200">
                    <Box
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Text color="color.text.subtlest" size="small">
                            Showing {allReportsList.length} reports
                        </Text>
                    </Box>

                    <DataTable
                        data={allReportsList}
                        columns={reportColumns}
                        emptyMessage="No reports found"
                        maxHeight="60vh"
                        getRowId={(row) => row.reportId}
                    />
                </Stack>
            </Stack>

            <NewReportDialog
                report={selectedReport}
                isOpen={isNewReportDialogOpen}
                onClose={() => {
                    setIsNewReportDialogOpen(false);
                    setSelectedReport(null);
                }}
            />
        </>
    );
}

export default Reports;
